import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Briefcase, Sparkles, Loader2, RefreshCw, ExternalLink, TrendingUp, Lightbulb, Target } from 'lucide-react';
import api from '../services/api';
import EnginePill from '../components/EnginePill';
import AIChat from '../components/AIChat';

const CompetitorIntelligence = () => {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState('');
  const [competitors, setCompetitors] = useState([]);
  const [competitorsLoading, setCompetitorsLoading] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const resp = await api.get('/websites');
        setWebsites(resp.data.websites);
        if (resp.data.websites?.length === 1) {
          setSelectedWebsite(resp.data.websites[0].id);
        }
      } catch (err) {
        setError('Failed to fetch websites');
      }
    };
    fetchWebsites();
  }, []);

  useEffect(() => {
    if (!selectedWebsite) {
      setCompetitors([]);
      setAnalysis(null);
      setSelectedDomain('');
      return;
    }
    const fetchCompetitors = async () => {
      setCompetitorsLoading(true);
      setError('');
      setAnalysis(null);
      setSelectedDomain('');
      try {
        const resp = await api.get(`/competitor-intelligence/${selectedWebsite}/competitors`);
        setCompetitors(resp.data.competitors || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load competitors');
      } finally {
        setCompetitorsLoading(false);
      }
    };
    fetchCompetitors();
  }, [selectedWebsite]);

  const runAnalysis = async (domain, force = false) => {
    setSelectedDomain(domain);
    setAnalysisLoading(true);
    setError('');
    setAnalysis(null);
    try {
      const resp = await api.post(`/competitor-intelligence/${selectedWebsite}/analyze`, { domain, force });
      setAnalysis(resp.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate analysis');
    } finally {
      setAnalysisLoading(false);
    }
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-bold tracking-tight">Competitor AI Intelligence</h1>
            <EnginePill />
          </div>
          <p className="text-muted-foreground mt-2">
            How AI describes your competitors, what content they win on, and where to push back.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Select a website
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Select value={selectedWebsite} onValueChange={setSelectedWebsite}>
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Pick a website to analyse" />
              </SelectTrigger>
              <SelectContent>
                {websites.map(w => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.brandName} ({w.domain})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {selectedWebsite && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Top competitors
              </CardTitle>
              <CardDescription>
                Domains ChatGPT cites as your competitors, ranked by citation count. Click "Analyse" to generate an AI report on how they're winning.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {competitorsLoading ? (
                <p className="text-sm text-muted-foreground">Loading competitors…</p>
              ) : competitors.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No competitor citations on this website yet. Run some keywords first — competitors show up here once ChatGPT cites them.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead className="w-32 text-right">Citations</TableHead>
                      <TableHead className="w-32 text-right">Keywords</TableHead>
                      <TableHead>Recent example</TableHead>
                      <TableHead className="w-32 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {competitors.map(c => (
                      <TableRow key={c.domain} className={selectedDomain === c.domain ? 'bg-primary/[0.04]' : ''}>
                        <TableCell className="font-medium">{c.domain}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="destructive">{c.citationCount}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm">{c.keywordCount}</TableCell>
                        <TableCell>
                          <p className="text-xs text-muted-foreground line-clamp-1">{c.recentTitle || '—'}</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant={selectedDomain === c.domain ? 'default' : 'outline'}
                            onClick={() => runAnalysis(c.domain)}
                            disabled={analysisLoading && selectedDomain === c.domain}
                          >
                            {analysisLoading && selectedDomain === c.domain ? (
                              <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Analysing…</>
                            ) : (
                              <><Sparkles className="h-3 w-3 mr-1" /> Analyse</>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {analysisLoading && !analysis && (
          <Card>
            <CardContent className="py-8 flex flex-col items-center gap-3 text-sm text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p>Reading {selectedDomain}'s citations and generating analysis…</p>
            </CardContent>
          </Card>
        )}

        {analysis && (
          <Card className="border-primary/30 bg-gradient-to-br from-background to-primary/[0.03]">
            <CardHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    AI Analysis: {analysis.domain}
                    {analysis.cached && (
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted px-1.5 py-0.5 rounded">cached</span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Based on {analysis.stats.totalCitations} citation{analysis.stats.totalCitations === 1 ? '' : 's'} across {analysis.stats.keywordsAffected} keyword{analysis.stats.keywordsAffected === 1 ? '' : 's'}.
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => runAnalysis(analysis.domain, true)}
                  disabled={analysisLoading}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="rounded-lg bg-muted/50 p-4">
                <p className="text-sm leading-relaxed">{analysis.analysis.summary}</p>
              </div>

              {analysis.analysis.sentimentTake && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Sentiment</h3>
                  <p className="text-sm text-muted-foreground">{analysis.analysis.sentimentTake}</p>
                  <div className="flex gap-3 text-xs mt-2">
                    <span className="text-emerald-700 font-medium">{analysis.stats.sentiment.positive} positive</span>
                    <span className="text-slate-600 font-medium">{analysis.stats.sentiment.neutral} neutral</span>
                    <span className="text-red-700 font-medium">{analysis.stats.sentiment.negative} negative</span>
                  </div>
                </div>
              )}

              {analysis.analysis.themes?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                    <TrendingUp className="h-4 w-4" />
                    What AI says about them
                  </h3>
                  <div className="space-y-2">
                    {analysis.analysis.themes.map((t, i) => (
                      <div key={i} className="rounded-md border bg-background p-3">
                        <p className="font-semibold text-sm">{t.theme}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t.evidence}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.analysis.dominanceMap?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Where they win</h3>
                  <div className="space-y-1.5">
                    {analysis.analysis.dominanceMap.map((d, i) => (
                      <div key={i} className="flex items-start gap-3 text-sm">
                        <span className="font-medium text-primary min-w-fit">"{d.keyword}"</span>
                        <span className="text-muted-foreground">— {d.verdict}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {analysis.analysis.contentPatterns?.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold mb-2">Content patterns</h3>
                  <ul className="space-y-1 text-sm">
                    {analysis.analysis.contentPatterns.map((p, i) => (
                      <li key={i} className="flex gap-2"><span className="text-muted-foreground mt-0.5">•</span><span>{p}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.analysis.actionsForUs?.length > 0 && (
                <div className="rounded-lg border-2 border-primary/30 bg-primary/[0.04] p-4">
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
                    <Lightbulb className="h-4 w-4 text-primary" />
                    What we should do
                  </h3>
                  <ol className="space-y-2 text-sm">
                    {analysis.analysis.actionsForUs.map((a, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">{i + 1}</span>
                        <span className="leading-relaxed">{a}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {selectedWebsite && <AIChat websiteId={selectedWebsite} />}
      </div>
    </TooltipProvider>
  );
};

export default CompetitorIntelligence;
